/**
 * Ocean Water Renderer
 * 
 * Creates animated water shimmer effects across the ocean.
 * Uses grid-aligned tiles with subtle color variations that
 * animate in a wave pattern to make the ocean feel alive.
 * 
 * Performance optimized:
 * - Only renders tiles in visible camera area
 * - Uses pooled graphics objects
 * - Slow animation tick rate
 */

import Phaser from 'phaser';
import { COLORS, TILE_SIZE, UNCHARTED_WATERS_BUFFER_PX, WORLD_HEIGHT, WORLD_WIDTH } from '../types';

// Configuration
const WATER_TILE_SIZE = TILE_SIZE;      // 16px tiles to match game grid
const ANIMATION_TICK_MS = 150;          // Animation frame rate
const WAVE_SPEED = 0.003;               // How fast waves travel
const SHIMMER_INTENSITY = 0.12;         // Max alpha variation

// Water color palette (subtle variations of deep ocean)
const WATER_COLORS = [
  0x0a1628,  // Base deep ocean
  0x0c1a2e,  // Slightly lighter
  0x0e1e34,  // Light variation
  0x08121e,  // Darker variation
  0x0b1826,  // Mid tone
];

const UNCHARTED_WATER_COLORS = [
  COLORS.unchartedDeepOcean,
  0x060f1a,
  0x07101c,
  0x040811,
  0x050c16,
];

interface WaterTile {
  graphics: Phaser.GameObjects.Graphics;
  tileX: number;
  tileY: number;
  colorIndex: number;
  phase: number;
}

export class OceanWaterRenderer {
  private scene: Phaser.Scene;
  private tiles: Map<string, WaterTile> = new Map();
  private animationTimer: Phaser.Time.TimerEvent | null = null;
  private globalTime: number = 0;
  private lastCameraX: number = -9999;
  private lastCameraY: number = -9999;
  private enabled: boolean = true;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    // Disabled: Ocean tileset now handles water rendering
    this.enabled = false;
  }

  /**
   * Start the water animation system
   */
  start(): void {
    this.animationTimer = this.scene.time.addEvent({
      delay: ANIMATION_TICK_MS,
      callback: () => this.updateAnimation(),
      loop: true,
    });
  }

  /**
   * Enable or disable the water effect
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearAllTiles();
    }
  }

  /**
   * Update visible water tiles based on camera position
   */
  updateVisibleArea(): void {
    if (!this.enabled) return;

    const camera = this.scene.cameras.main;
    const cameraX = Math.floor(camera.scrollX / WATER_TILE_SIZE) * WATER_TILE_SIZE;
    const cameraY = Math.floor(camera.scrollY / WATER_TILE_SIZE) * WATER_TILE_SIZE;

    // Only update if camera moved significantly
    if (Math.abs(cameraX - this.lastCameraX) < WATER_TILE_SIZE &&
        Math.abs(cameraY - this.lastCameraY) < WATER_TILE_SIZE) {
      return;
    }

    this.lastCameraX = cameraX;
    this.lastCameraY = cameraY;

    // Calculate visible bounds with padding
    const padding = WATER_TILE_SIZE * 2;
    const left = Math.floor((camera.scrollX - padding) / WATER_TILE_SIZE);
    const top = Math.floor((camera.scrollY - padding) / WATER_TILE_SIZE);
    const right = Math.ceil((camera.scrollX + camera.width + padding) / WATER_TILE_SIZE);
    const bottom = Math.ceil((camera.scrollY + camera.height + padding) / WATER_TILE_SIZE);

    // Track which tiles should exist
    const visibleKeys = new Set<string>();

    // Create or update tiles in visible area
    for (let tileX = left; tileX <= right; tileX++) {
      for (let tileY = top; tileY <= bottom; tileY++) {
        const key = `${tileX},${tileY}`;
        visibleKeys.add(key);

        if (!this.tiles.has(key)) {
          this.createTile(tileX, tileY);
        }
      }
    }

    // Remove tiles outside visible area
    const keysToRemove: string[] = [];
    this.tiles.forEach((_, key) => {
      if (!visibleKeys.has(key)) {
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach(key => {
      const tile = this.tiles.get(key);
      if (tile) {
        tile.graphics.destroy();
        this.tiles.delete(key);
      }
    });
  }

  /**
   * Create a water tile at the given grid position
   */
  private createTile(tileX: number, tileY: number): void {
    const key = `${tileX},${tileY}`;

    // Deterministic color based on position (creates natural-looking variation)
    const seed = (tileX * 7919 + tileY * 104729) % 1000;
    const colorIndex = seed % WATER_COLORS.length;
    const phase = (seed / 1000) * Math.PI * 2;

    const graphics = this.scene.add.graphics();
    graphics.setDepth(-9);  // Above base ocean (-10), below grid (-5)

    const tile: WaterTile = {
      graphics,
      tileX,
      tileY,
      colorIndex,
      phase,
    };

    this.tiles.set(key, tile);
    this.drawTile(tile);
  }

  /**
   * Draw a single water tile with current animation state
   */
  private drawTile(tile: WaterTile): void {
    const { graphics, tileX, tileY, colorIndex, phase } = tile;
    const worldX = tileX * WATER_TILE_SIZE;
    const worldY = tileY * WATER_TILE_SIZE;

    graphics.clear();

    // Calculate wave effect based on position and time
    const waveX = Math.sin(this.globalTime + tileX * 0.5 + phase) * 0.5 + 0.5;
    const waveY = Math.cos(this.globalTime * 0.7 + tileY * 0.4 + phase) * 0.5 + 0.5;
    const combinedWave = (waveX + waveY) / 2;

    const isUncharted: boolean =
      worldX < -UNCHARTED_WATERS_BUFFER_PX ||
      worldY < -UNCHARTED_WATERS_BUFFER_PX ||
      worldX >= WORLD_WIDTH + UNCHARTED_WATERS_BUFFER_PX ||
      worldY >= WORLD_HEIGHT + UNCHARTED_WATERS_BUFFER_PX;

    // Base color with wave-based alpha variation
    const palette = isUncharted ? UNCHARTED_WATER_COLORS : WATER_COLORS;
    const baseColor = palette[colorIndex % palette.length];
    const alpha = 0.3 + combinedWave * SHIMMER_INTENSITY;

    // Draw main tile
    graphics.fillStyle(baseColor, alpha);
    graphics.fillRect(worldX, worldY, WATER_TILE_SIZE, WATER_TILE_SIZE);

    // Add highlight on wave peaks (subtle lighter squares)
    if (!isUncharted && combinedWave > 0.6) {
      const highlightAlpha = (combinedWave - 0.6) * 0.3;
      graphics.fillStyle(0x1a3a5a, highlightAlpha);
      
      // Draw small highlight squares in grid pattern
      const subSize = TILE_SIZE / 2;
      const offsetX = Math.floor((this.globalTime * 10 + tileX) % 2) * subSize;
      const offsetY = Math.floor((this.globalTime * 8 + tileY) % 2) * subSize;
      
      graphics.fillRect(
        worldX + offsetX + 2,
        worldY + offsetY + 2,
        subSize - 4,
        subSize - 4
      );
    }

    // Add subtle foam lines on high waves
    if (!isUncharted && combinedWave > 0.75) {
      const foamAlpha = (combinedWave - 0.75) * 0.4;
      graphics.lineStyle(1, 0x3a5a7a, foamAlpha);
      
      // Horizontal foam line
      const lineY = worldY + WATER_TILE_SIZE * 0.3;
      graphics.beginPath();
      graphics.moveTo(worldX + 4, lineY);
      graphics.lineTo(worldX + WATER_TILE_SIZE - 4, lineY);
      graphics.strokePath();
    }
  }

  /**
   * Update animation for all visible tiles
   */
  private updateAnimation(): void {
    if (!this.enabled) return;

    this.globalTime += WAVE_SPEED * ANIMATION_TICK_MS;

    this.tiles.forEach(tile => {
      this.drawTile(tile);
    });
  }

  /**
   * Clear all tiles
   */
  private clearAllTiles(): void {
    this.tiles.forEach(tile => {
      tile.graphics.destroy();
    });
    this.tiles.clear();
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.animationTimer) {
      this.animationTimer.destroy();
      this.animationTimer = null;
    }
    this.clearAllTiles();
  }
}
